/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { DIALOG_IDS } from 'constants';

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages } from 'react-intl';
import { Formik } from 'formik';
import { Form, FormGroup, RadioButtonGroup, RadioButton } from 'carbon-addons-iot-react';

import { determineStatusString } from '../../utils/alertUtils';
import { reduxDialog } from '../../containers/ReduxDialog/ReduxDialog';

import { formatAppError } from 'Utils';
import { BaseModal } from 'Common';

const propTypes = {
  payload: PropTypes.shape({
    /** an array of alert ids whose status you should change */
    alertIds: PropTypes.arrayOf(PropTypes.number),
    /** an array of available status strings */
    availableStatusValues: PropTypes.arrayOf(PropTypes.string),
    onChangeStatus: PropTypes.func.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
};

const messages = defineMessages({
  title: 'Bulk Change Status',
  helpText: 'Choose the status to change the items to.',
  status: 'Status:',
  errorUpdating: 'Error updating alerts',
  close: 'Close notification',
});

const ChangeAlertStatusModal = ({
  payload: { availableStatusValues, alertIds, onChangeStatus },
  onClose,
  intl: { formatMessage },
  ...others
}) => {
  const [error, setError] = useState();

  // BaseModal can only take a single string as an error message so define the formatted error here
  // then concat it in the modal
  let errorMessage;
  if (error) {
    errorMessage = { ...formatAppError(messages.errorUpdating, error, formatMessage) };
  }

  return (
    <Formik
      initialValues={{ status: availableStatusValues[0] }}
      onSubmit={values =>
        onChangeStatus(values?.status, alertIds)
          .then(() => {
            // if no error is thrown close the dialog
            onClose();
          })
          .catch(errorResponse => {
            setError(errorResponse);
          })
      }>
      {({ values, isSubmitting, handleChange, handleSubmit }) => (
        <BaseModal
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...others}
          className="changeAlertStatusModal"
          size="xs"
          header={{
            helpText: formatMessage(messages.helpText),
            title: formatMessage(messages.title),
          }}
          onClose={onClose}
          footer={{ isPrimaryButtonDisabled: isSubmitting }}
          onSubmit={handleSubmit}
          error={error ? `${errorMessage.title} - ${errorMessage.subtitle}` : null}>
          <Form onSubmit={handleSubmit}>
            <FormGroup legendText={formatMessage(messages.status)}>
              <RadioButtonGroup
                defaultSelected={availableStatusValues[0]}
                id="status"
                name="status"
                onChange={(value, name, evt) => {
                  handleChange(evt);
                }}
                orientation="vertical"
                valueSelected={values.status}>
                {availableStatusValues.map(status => (
                  <RadioButton
                    key={status}
                    id={status}
                    labelText={determineStatusString(status, formatMessage)}
                    value={status}
                  />
                ))}
              </RadioButtonGroup>
            </FormGroup>
          </Form>
        </BaseModal>
      )}
    </Formik>
  );
};

ChangeAlertStatusModal.propTypes = propTypes;
ChangeAlertStatusModal.defaultProps = {
  isOpen: false,
};

export default injectIntl(
  reduxDialog({ name: DIALOG_IDS.CHANGE_ALERT_STATUS_MODAL })(ChangeAlertStatusModal)
);
